import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { getUsers, assignAccessGroupsToUser, User } from 'services/user';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';

type Props = {
  open: boolean;
  accessGroupId: number | null;
  onClose: () => void;
  onAssigned?: () => void;
};

export default function AssignUsersModal({ open, accessGroupId, onClose, onAssigned }: Props) {
  const DEBOUNCE_MS = 500;

  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [options, setOptions] = useState<User[]>([]);
  const [selected, setSelected] = useState<User[]>([]);
  const [search, setSearch] = useState<string>('');
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isSubmitting, setSubmitting] = useState<boolean>(false);
  const [page] = useState<number>(1);
  const [perPage] = useState<number>(50);

  const fetchUsers = useCallback(
    (q: string) => {
      setLoading(true);
      getUsers(
        data => {
          setOptions(data || []);
          setLoading(false);
        },
        { perPage, page, search: q || '' }
      );
    },
    [page, perPage]
  );

  useEffect(() => {
    if (!open) return;

    if (!search) {
      fetchUsers('');
      return;
    }

    const handler = window.setTimeout(() => {
      fetchUsers(search);
    }, DEBOUNCE_MS);

    clearTimeout(handler);
  }, [open, fetchUsers, search]);

  useEffect(() => {
    if (!selected || selected.length === 0) return;

    setOptions(prev => {
      const prevById = new Map(prev.map(o => [o.id, o]));
      let changed = false;
      const merged = [...prev];

      selected.forEach(s => {
        if (!prevById.has(s.id)) {
          merged.push(s);
          changed = true;
        }
      });

      return changed ? merged : prev;
    });
  }, [selected]);

  const handleAutocompleteChange = useCallback((_: any, newValue: User[] | null) => {
    setSelected(newValue || []);
  }, []);

  const getOptionLabel = useCallback((opt: User) => `${opt.name}${opt.email ? ` — ${opt.email}` : ''}`, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  }, []);

  const renderInputFn = useCallback(
    (params: any) => (
      <TextField
        {...params}
        label='Buscar usuários por nome ou e-mail'
        placeholder='Digite para buscar...'
        onChange={handleSearchChange}
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              {isLoading ? <CircularProgress color='inherit' size={20} /> : null}
              {params.InputProps.endAdornment}
            </>
          )
        }}
        fullWidth
      />
    ),
    [handleSearchChange, isLoading]
  );

  const handleConfirm = useCallback(() => {
    if (!accessGroupId) {
      enqueueSnackbar('ID do grupo de acesso não disponível.', { variant: 'error' });
      return;
    }
    if (selected.length === 0) {
      enqueueSnackbar('Selecione pelo menos um usuário.', { variant: 'warning' });
      return;
    }

    setSubmitting(true);
    const ids = selected.map(u => u.id);
    assignAccessGroupsToUser(
      accessGroupId,
      ids,
      () => {
        setSubmitting(false);
        enqueueSnackbar('Usuários adicionados com sucesso.', { variant: 'success' });
        if (onAssigned) onAssigned();
        onClose();
        navigate('/grupos-acesso');
      },
      errMsg => {
        setSubmitting(false);
        enqueueSnackbar(errMsg, { variant: 'error' });
      }
    );
  }, [accessGroupId, selected, enqueueSnackbar, onAssigned, onClose, navigate]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Adicionar usuários ao Grupo de Acesso</DialogTitle>
      <DialogContent dividers>
        <Box mb={2}>
          <Autocomplete
            multiple
            options={options}
            getOptionLabel={getOptionLabel}
            value={selected}
            onChange={handleAutocompleteChange}
            filterSelectedOptions
            noOptionsText='Nenhum usuários encontrado'
            loadingText='Carregando...'
            size='small'
            loading={isLoading}
            isOptionEqualToValue={(option: User, value: User) => option.id === value.id}
            renderInput={renderInputFn}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: selected.length > 0 ? 4 : 10 }
            }}
          />
        </Box>

        <Box fontSize='0.9rem' color='text.secondary'>
          Você pode selecionar um ou mais usuários, busque o nome/email e selecione-o.
        </Box>
      </DialogContent>

      <DialogActions style={{ paddingTop: 5, marginBottom: 10, marginRight: 12 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant='contained'
          color='primary'
          sx={{ borderRadius: 10, whiteSpace: 'nowrap' }}
          disableElevation
          onClick={handleConfirm}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} color='inherit' /> : null}
        >
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
